import { LinkOutlined, DownloadOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import * as Icons from '@ant-design/icons';
import { App, ConfigProvider } from 'antd';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { getAdminInfo } from '@/services/property_api/adminAuthController';
import { getUserMenuTree } from '@/services/property_api/sysMenuController';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/user/login';

// 将图标字符串转换为 React 组件
const getIconComponent = (iconName?: string) => {
  if (!iconName) return undefined;

  // 处理可能的图标名称格式
  const iconKey = iconName.charAt(0).toUpperCase() + iconName.slice(1);
  const IconComponent = (Icons as any)[iconKey];

  if (IconComponent) {
    return React.createElement(IconComponent);
  }

  // 如果找不到对应的图标，返回默认图标
  return React.createElement(Icons.MenuOutlined);
};

// 转换菜单数据为 ProLayout 需要的格式
const transformMenuData = (menuData: API.SysMenuTreeVo[], parentEnName?: string): any[] => {
  return menuData
    .filter(menu => menu.isShow === '1' && menu.type === 1) // 只显示菜单类型且显示的项目
    .map(menu => {
      // 直接使用 enName，框架会自动拼接 menu. 前缀
      const enName = menu.enName || menu.name?.toLowerCase().replace(/\s+/g, '-') || 'unnamed';

      console.log(`菜单处理: ${menu.name} (enName: ${enName})`);

      const menuItem: any = {
        path: menu.href,
        name: enName, // 直接使用 enName，框架会自动处理
        icon: getIconComponent(menu.icon),
        component: menu.component,
        sort: menu.sort || 0,
      };

      // 如果有子菜单，递归处理，传递当前菜单的 enName 作为父级
      if (menu.children && menu.children.length > 0) {
        menuItem.routes = transformMenuData(menu.children, enName);
      }

      return menuItem;
    })
    .sort((a, b) => a.sort - b.sort); // 按排序字段排序
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
  menuData?: API.SysMenuTreeVo[];
  permissions?: string[];
  roles?: string[];
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await getAdminInfo({
        skipErrorHandler: true,
      });
      // 从返回数据中提取用户信息和权限
      if (msg.success && msg.data) {
        // 提取用户信息
        const userInfo = msg.data.userInfo ? {
          name: msg.data.userInfo.name,
          avatar: msg.data.userInfo.avatar || 'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
          userid: msg.data.userInfo.id,
          email: msg.data.userInfo.email,
          ...msg.data.userInfo
        } : msg.data;

        // 返回用户信息、权限和角色
        return {
          userInfo,
          permissions: msg.data.permissions || [],
          roles: msg.data.roles || [],
        };
      }
      return { userInfo: msg.data, permissions: [], roles: [] };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchUserMenus = async () => {
    try {
      const response = await getUserMenuTree({
        skipErrorHandler: true,
      });
      if (response.success && response.data) {
        return response.data;
      }
    } catch (_error) {
      console.error('获取用户菜单失败:', _error);
    }
    return [];
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const userData = await fetchUserInfo();
    const currentUser = userData?.userInfo;
    const permissions = userData?.permissions || [];
    const roles = userData?.roles || [];
    const menuData = currentUser ? await fetchUserMenus() : [];
    return {
      fetchUserInfo,
      currentUser,
      menuData,
      permissions,
      roles,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    // 动态菜单配置
    menu: {
      locale: true,
      request: async () => {
        console.log('菜单请求被调用，初始状态:', initialState);

        // 如果有菜单数据，使用动态菜单；否则返回空数组
        if (initialState?.menuData && initialState.menuData.length > 0) {
          console.log('原始菜单数据:', initialState.menuData);

          const transformedMenu = transformMenuData(initialState.menuData);
          console.log('转换后的菜单数据:', transformedMenu);

          return transformedMenu;
        }

        console.log('没有菜单数据，返回空数组');
        return [];
      },
    },
    actionsRender: () => [
      // <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // links: isDevOrTest
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    menuHeaderRender: undefined,
    // 修改菜单项渲染函数，确保只显示一个图标
    menuItemRender: (menuItemProps, defaultDom) => {
      const handleMenuClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (menuItemProps.path && menuItemProps.path !== '#') {
          history.push(menuItemProps.path);
        }
      };

      if (!menuItemProps.icon) {
        return (
          <a onClick={handleMenuClick}>
            {defaultDom}
          </a>
        );
      }

      return (
        <a onClick={handleMenuClick} style={{ display: 'flex', alignItems: 'center' }}>
          {menuItemProps.icon}
          <span style={{ marginLeft: '10px' }}>{defaultDom}</span>
        </a>
      );
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {/* {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )} */}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: isDev ? '' : '',
  ...errorConfig,
};

/**
 * @name rootContainer 配置
 * @description 在应用最外层包裹一个容器组件
 */
export function rootContainer(container: React.ReactElement) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Image: {
            // 自定义Image组件的样式
          },
        },
      }}
      image={{
        preview: {
          toolbarRender: (
            _,
            {
              transform: { scale },
              actions: {
                onFlipY,
                onFlipX,
                onRotateLeft,
                onRotateRight,
                onZoomOut,
                onZoomIn,
                onReset,
              },
              current,
              total,
            },
          ) => (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <DownloadOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={() => {
                  // 获取当前图片的src
                  const images = document.querySelectorAll('.ant-image-preview-img');
                  if (images[current]) {
                    const imgSrc = (images[current] as HTMLImageElement).src;
                    // 创建一个临时的a标签来下载
                    const link = document.createElement('a');
                    link.href = imgSrc;
                    link.download = `image-${current + 1}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
              />
              <Icons.SwapOutlined
                rotate={90}
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={onFlipY}
              />
              <Icons.SwapOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={onFlipX}
              />
              <Icons.RotateLeftOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={onRotateLeft}
              />
              <Icons.RotateRightOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={onRotateRight}
              />
              <Icons.ZoomOutOutlined
                disabled={scale === 1}
                style={{ fontSize: 20, cursor: scale === 1 ? 'not-allowed' : 'pointer', opacity: scale === 1 ? 0.5 : 1 }}
                onClick={onZoomOut}
              />
              <Icons.ZoomInOutlined
                disabled={scale === 50}
                style={{ fontSize: 20, cursor: scale === 50 ? 'not-allowed' : 'pointer', opacity: scale === 50 ? 0.5 : 1 }}
                onClick={onZoomIn}
              />
              <Icons.UndoOutlined
                style={{ fontSize: 20, cursor: 'pointer' }}
                onClick={onReset}
              />
              <span style={{ marginLeft: 12 }}>
                {current + 1} / {total}
              </span>
            </div>
          ),
        },
      }}
    >
      <App>
        {container}
      </App>
    </ConfigProvider>
  );
}
